import './style.modules.scss'

import { BrandIcon, BrandIconColor, BrandIconId } from '@ampcore/adel-react'
import { Spacer } from '@ampcore/adel-react'
import { SpacerSpacing } from '@ampcore/adel-react'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import xss from 'xss'

import { AssessmentResult } from '../AssessmentResult/AssessmentResult'
import { AssessmentProgressBarColor } from './AssessmentProgressBarColor.type'

export type Answer = {
  answer: string
  points: string
  category: string
  condition: string
  categoryPoints: string
}

export type QuestionItem = {
  item: string
  answer: Answer[]
}

export type ResultItem = {
  condition: string
  category: string
  points: string
  content: string
  resultCategory: string
}

export type AssessmentProps = {
  intro: string
  icon?: BrandIconId
  iconColor?: BrandIconColor
  progressBarColor: AssessmentProgressBarColor
  grayFacet?: string
  componentSpacing?: SpacerSpacing
  questions: QuestionItem[]
  caption: string
  documentId: string
  documentLastModified?: string
  fill?: boolean
  stroke?: boolean
  results: ResultItem[]
  disclosureText?: string
  overrideRecipient?: string
  emailSubject?: string
  emailGreeting?: string
  emailBody?: string
  submitError?: string
  nextError?: string
  analyticsName?: string
  testId?: string
}

const getProgressBarColor = (color: AssessmentProgressBarColor) => {
  const colorMap = {
    mint: '--color-mint',
    'dark-mint': '--color-dark-mint',
    lime: '--color-lime',
    'midnight-blue': '--color-midnight-blue',
    purple: '--color-purple',
    orange: '--color-orange',
    berry: '--color-berry'
  }
  return colorMap[color] || '--color-dark-mint'
}

export const Assessment = (props: AssessmentProps) => {
  const {
    icon,
    iconColor,
    intro,
    questions,
    progressBarColor,
    grayFacet,
    componentSpacing,
    caption,
    results,
    documentId,
    documentLastModified,
    fill,
    stroke,
    emailSubject,
    emailGreeting,
    emailBody,
    disclosureText,
    overrideRecipient,
    submitError,
    nextError,
    analyticsName,
    testId
  } = props
  const atb = useMemo(
    () =>
      new AnalyticsTagBuilder()
        .setDefaultName('Assessment')
        .setAnalyticsName(analyticsName),
    [analyticsName]
  )
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<number, number>
  >({})
  const [submitting, setSubmitting] = useState(false)
  const [showResult, setShowResult] = useState(false)
  const [isFromStorage, setIsFromStorage] = useState(false)
  const [isNextAttempted, setIsNextAttempted] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  )
  const errorRef = useRef<HTMLParagraphElement | null>(null)
  const totalQuestions = questions.length
  const currentQuestion = questions[currentQuestionIndex]
  const isAnswerSelected = currentQuestionIndex in selectedAnswers
  const brandIconTestId = testId ? `${testId}-icon` : undefined
  const isLastQuestion = currentQuestionIndex >= totalQuestions - 1
  const showFacetBackground = grayFacet !== 'false'
  const submitButtonText = submitting ? 'Submitting...' : 'Submit'
  const progressPercentage = ((currentQuestionIndex + 1) / totalQuestions) * 100
  const shouldShowResult = isFromStorage || showResult
  const actionBtnBaseClass = 'Button Button--primary u-sm-marginBottom8'
  const nextButtonClass = `Assessment-nextBtn ${actionBtnBaseClass} ${
    !isAnswerSelected ? ' is-disabled' : ''
  }`
  const submitButtonClass = `Button Button--primary u-sm-marginBottom8 ${
    !isAnswerSelected ? ' is-disabled' : ''
  }`
  const facetBackgroundStyle = {
    ...(showFacetBackground && {
      background: `linear-gradient(170deg, rgba(255, 255, 255, 1) 70%, rgba(255, 255, 255, 0) 135%), url(${window.AMPCOMPATH}images/facets/quiz/gray-white-facet.svg)`
    })
  }
  const shouldShowError = isNextAttempted && !isAnswerSelected
  const formFieldsetClassName = [
    'Form-group',
    'u-marginTop8',
    shouldShowError ? 'Assessment-radioGroup--error' : 'u-marginBottom48'
  ].join(' ')

  const radioInputClassName = [
    'Radio-button',
    'Input',
    shouldShowError ? 'Input--error' : ''
  ].join(' ')

  useEffect(() => {
    const storedAnswers = localStorage.getItem(documentId)
    const parsed = storedAnswers && JSON.parse(storedAnswers)
    const isStoredAnswersValid = parsed?.timestamp === documentLastModified
    !isStoredAnswersValid && localStorage.removeItem(documentId)
    const handleValid = () => {
      setSelectedAnswers(parsed.answers)
      setIsFromStorage(true)
    }
    isStoredAnswersValid && handleValid()
  }, [documentId, documentLastModified])

  useEffect(() => {
    if (errorMessage && errorRef.current) {
      errorRef.current.focus()
    }
  }, [errorMessage])

  const handleAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedIndex = parseInt(e.target.value, 10)
    setSelectedAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: selectedIndex
    }))
    setIsNextAttempted(false)
    setErrorMessage(undefined)
  }

  const handleNext = () => {
    setIsNextAttempted(true)
    const hasNextQuestion = isAnswerSelected && !isLastQuestion
    hasNextQuestion && goToNextQuestion()
    !hasNextQuestion && setErrorMessage(nextError)
  }

  const goToNextQuestion = () => {
    setIsNextAttempted(false)
    setCurrentQuestionIndex(currentQuestionIndex + 1)
  }

  const handleRetake = () => {
    setCurrentQuestionIndex(0)
    setSelectedAnswers({})
    localStorage.removeItem(documentId)
    setIsFromStorage(false)
    setShowResult(false)
  }

  const handleSubmit = () => {
    setIsNextAttempted(true)
    isAnswerSelected && submitAnswers()
    !isAnswerSelected && setErrorMessage(submitError)
  }

  const submitAnswers = async () => {
    localStorage.setItem(
      documentId,
      JSON.stringify({
        timestamp: documentLastModified,
        answers: selectedAnswers
      })
    )
    setIsFromStorage(false)
    setSubmitting(true)
    setTimeout(() => {
      setShowResult(true)
      setSubmitting(false)
      setIsNextAttempted(false)
    }, 100)
  }
  const renderAssessmentForm = () => (
    <Spacer spacing={componentSpacing}>
      <section className="Assessment u-sizeConstrained">
        <div className="Card" style={facetBackgroundStyle}>
          <div className="Card-body u-sm-padding20">
            <div className="Assessment-intro">
              {icon && (
                <BrandIcon
                  icon={icon}
                  color={iconColor ?? BrandIconColor.NONE}
                  fill={fill ? true : false}
                  stroke={stroke ? true : false}
                  className={`BrandIcon--sizeMedium`}
                  testId={brandIconTestId}
                />
              )}
              <div
                className="u-marginTop8 u-marginBottom20"
                dangerouslySetInnerHTML={{ __html: xss(intro) }}
              ></div>
            </div>
            <div className="Assessment-content">
              <div
                className="ProgressBar u-marginTop40 u-marginBottom32"
                style={{
                  color: `var(${getProgressBarColor(progressBarColor)})`
                }}
              >
                <p className="ProgressBar-label">
                  Question {currentQuestionIndex + 1} of {totalQuestions}
                </p>
                <div className="ProgressBar-container">
                  <div className="ProgressBar-track">
                    <span
                      className="ProgressBar-fill"
                      style={{ width: `${progressPercentage}%` }}
                      role="progressbar"
                      aria-label="Progress bar"
                    ></span>
                  </div>
                </div>
              </div>
              <form className="Form Assessment-form">
                <fieldset className={formFieldsetClassName} role="radiogroup">
                  <legend className="Type-4 u-marginBottom8">
                    {currentQuestion.item}
                  </legend>
                  {currentQuestion.answer.map((ans, index) => (
                    <div className="Radio" key={index}>
                      <input
                        className={radioInputClassName}
                        id={`question-${currentQuestionIndex}-${index}`}
                        type="radio"
                        value={index}
                        name={`question-${currentQuestionIndex}`}
                        checked={
                          selectedAnswers[currentQuestionIndex] === index
                        }
                        onChange={handleAnswerChange}
                      />
                      <label
                        htmlFor={`question-${currentQuestionIndex}-${index}`}
                        className="u-flex u-flexAlignItemsCenter Radio-label"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          height="24px"
                          width="24px"
                          viewBox="0 -960 960 960"
                        >
                          <defs>
                            <mask id="hole">
                              <circle
                                r="100%"
                                cx="480"
                                cy="-480"
                                fill="white"
                              />
                              <circle cx="480" cy="-480" r="259" fill="black" />
                            </mask>
                          </defs>
                          <circle
                            cx="480"
                            cy="-480"
                            r="315"
                            fill="rgb(31 34 38)"
                            mask="url(#hole)"
                          />
                          <circle
                            className="Radio-dot"
                            cx="480"
                            cy="-480"
                            r="145"
                            fill="rgb(31 34 38)"
                          />
                        </svg>
                        <span className="u-textPrimary">{ans.answer}</span>
                      </label>
                    </div>
                  ))}
                </fieldset>
                {errorMessage && (
                  <p
                    ref={errorRef}
                    className={
                      'Form-help Form-help--error Assessment-radioGroup--error'
                    }
                    tabIndex={-1}
                    role="alert"
                    aria-live="assertive"
                  >
                    <span aria-hidden="true">warning</span>
                    <span>{errorMessage}</span>
                  </p>
                )}
              </form>
              <div className="Assessment-action u-flex u-flexJustifyBetween u-sm-flexCol">
                {!isLastQuestion && (
                  <button
                    className={nextButtonClass}
                    onClick={handleNext}
                    aria-disabled={!isAnswerSelected}
                    {...atb.dataAnalyticAttribute(
                      'anchor-next-question-button'
                    )}
                    data-testid={testId ? `${testId}-next-btn` : undefined}
                  >
                    Next Question
                  </button>
                )}
                {isLastQuestion && (
                  <button
                    className={submitButtonClass}
                    aria-disabled={!isAnswerSelected || submitting}
                    onClick={handleSubmit}
                    {...atb.dataAnalyticAttribute(
                      'risk-tolerance-submit-button'
                    )}
                  >
                    {submitButtonText}
                  </button>
                )}
                {currentQuestionIndex > 0 && (
                  <button
                    className="Button Button--secondary Button--secondary--action-blue"
                    onClick={() =>
                      setCurrentQuestionIndex(prev => Math.max(prev - 1, 0))
                    }
                    {...atb.dataAnalyticAttribute('anchor-back-button')}
                  >
                    Back
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Spacer>
  )

  const renderAssessmentResult = () => (
    <AssessmentResult
      questions={questions}
      selectedAnswers={selectedAnswers}
      caption={caption}
      results={results}
      icon={icon}
      iconColor={iconColor}
      fill={fill}
      stroke={stroke}
      componentSpacing={componentSpacing}
      showFacetBackground={showFacetBackground}
      onRetake={handleRetake}
      clientInquiryEmail={{
        subject: emailSubject,
        greeting: emailGreeting,
        body: emailBody
      }}
      disclosureText={disclosureText}
      overrideRecipient={overrideRecipient}
      analyticsName={analyticsName}
    />
  )

  return shouldShowResult ? renderAssessmentResult() : renderAssessmentForm()
}
